import { Button, useNotifications } from '@ph-react-ui/core';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useGetEmailLastSent } from '../../../hooks/settings/admin/useGetEmailLastSent';
import { useGetEmailNotificationSettings } from '../../../hooks/settings/admin/useGetEmailNotificationSettings';
import { useResendEmailNotification } from '../../../hooks/settings/admin/useResendEmailNotification';
import { useUpdateEmailNotificationSettings } from '../../../hooks/settings/admin/useUpdateEmailNotificationSettings';
import type { EmailNotificationSettingsDto } from '../../../models/settings/admin/email-notification-settings.dto';
import { Input } from '../../../utils/components/hoc-components';
import { EmailNotificationResendType } from '../../../utils/enums/email-notification-resend-type.enum';
import {
  getCommonValidators,
  VALIDATION as V,
} from '../../../utils/inputValidators';
import { ResendButton } from '../../shared/buttons/ResendEmailNotificationButton';
import { FormSkeleton } from '../../shared/components/skeletons/FormSkeleton';

export function SchoolPaymentNotificationSettings() {
  const { t } = useTranslation();
  const { data: settings, isPending: isSettingsPending } =
    useGetEmailNotificationSettings();
  const emailLastSentQueries = useGetEmailLastSent();
  const queryClient = useQueryClient();
  const notification = useNotifications();
  const updateSettings = useUpdateEmailNotificationSettings();
  const resendEmailNotification = useResendEmailNotification();

  const methods = useForm<EmailNotificationSettingsDto>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      daysBeforeDueDate: 0,
      daysBeforeDueDateNotification: '',
      daysAfterDueDate: 0,
      daysAfterDueDateNotification: '',
      daysAfterGracePeriod: 0,
      daysAfterGracePeriodNotification: '',
    },
  });

  const beforeDueDateNotification = methods.watch(
    'daysBeforeDueDateNotification'
  );
  const afterDueDateNotification = methods.watch(
    'daysAfterDueDateNotification'
  );
  const afterGracePeriodNotification = methods.watch(
    'daysAfterGracePeriodNotification'
  );

  const { isDirty, errors } = methods.formState;

  const notificationContentRules = useMemo(
    () => getCommonValidators([V.REQUIRED]),
    []
  );
  const daysRules = useMemo(
    () => getCommonValidators([V.REQUIRED, V.NUMBER_PATTERN, V.MIN_0]),
    []
  );

  const hasErrors = Object.keys(errors).length > 0;
  const isEmpty = useCallback((value?: string) => !value || !value.trim(), []);
  const isSubmitting = updateSettings.isPending;
  const isResendBusy = resendEmailNotification.isPending;

  const onSubmit = useCallback(
    (data: EmailNotificationSettingsDto) => {
      updateSettings
        .mutateAsync(data)
        .then(() => {
          notification.success(
            t('SETTINGS.MESSAGES.NOTIFICATION_SETTINGS_UPDATED')
          );

          queryClient.invalidateQueries({
            queryKey: ['payment-email-notification-settings'],
          });

          methods.reset(data);
        })
        .catch((error: any) => {
          notification.danger(t(`NETWORK_ERRORS.${error.errors?.[0]}`));
        });
    },
    [updateSettings, notification, t, queryClient, methods]
  );

  const onResend = useCallback(
    (type: EmailNotificationResendType) => {
      resendEmailNotification
        .mutateAsync(type)
        .then(() => {
          notification.success(
            t('SETTINGS.MESSAGES.NOTIFICATION_RESENT_SUCCESS')
          );
          // Invalidate the last sent date query for this type
          queryClient.invalidateQueries({
            queryKey: ['email-last-sent', type],
          });
        })
        .catch((error: any) =>
          notification.danger(t(`NETWORK_ERRORS.${error.errors?.[0]}`))
        );
    },
    [resendEmailNotification, notification, t, queryClient]
  );

  useEffect(() => {
    if (settings) {
      methods.reset(settings);
    }
  }, [settings, methods]);

  if (isSettingsPending) {
    return <FormSkeleton />;
  }

  return (
    <FormProvider {...methods}>
      <form className="settings pt-4" onSubmit={methods.handleSubmit(onSubmit)}>
        {/* 1) XX days BEFORE due date */}
        <div className="settings__section">
          <h2 className="settings__section__title">
            {t('SETTINGS.TITLES.NOTIFICATION_BEFORE_DUE_DATE')}
          </h2>

          <div className="notification-row">
            <label className="settings__section__item__label notification-row__label">
              {t('SETTINGS.LABELS.DAYS_BEFORE_DUE_DATE')}
            </label>
            <Input
              name="daysBeforeDueDate"
              className="settings__section__item__input notification-row__input"
              rules={daysRules}
              placeholder={t('SETTINGS.LABELS.DAYS_COUNT') ?? ''}
            />
            <ResendButton
              isDirty={isDirty}
              isEmpty={isEmpty(beforeDueDateNotification)}
              isSubmitting={isSubmitting}
              isResendLoading={
                isResendBusy &&
                resendEmailNotification.variables ===
                  EmailNotificationResendType.daysBeforeDueDate
              }
              isResendBusy={isResendBusy}
              onClick={() =>
                onResend(EmailNotificationResendType.daysBeforeDueDate)
              }
              tooltipMessage={t('SETTINGS.TOOLTIPS.SAVE_TO_ENABLE_RESEND')}
              buttonText={t('ACTIONS.RESEND')}
              lastSentDate={emailLastSentQueries[0]?.data as string}
            />
          </div>

          <div className="notification-row__textarea">
            <textarea
              className="settings__section__item__textarea"
              {...methods.register(
                'daysBeforeDueDateNotification',
                notificationContentRules
              )}
              placeholder={t('SETTINGS.LABELS.EMAIL_CONTENT') ?? ''}
            />
          </div>
        </div>

        {/* 2) XX days AFTER due date */}
        <div className="settings__section">
          <h2 className="settings__section__title">
            {t('SETTINGS.TITLES.NOTIFICATION_AFTER_DUE_DATE')}
          </h2>

          <div className="notification-row">
            <label className="settings__section__item__label notification-row__label">
              {t('SETTINGS.LABELS.DAYS_AFTER_DUE_DATE')}
            </label>
            <Input
              name="daysAfterDueDate"
              className="settings__section__item__input notification-row__input"
              rules={daysRules}
              placeholder={t('SETTINGS.LABELS.DAYS_COUNT') ?? ''}
            />
            <ResendButton
              isDirty={isDirty}
              isEmpty={isEmpty(afterDueDateNotification)}
              isSubmitting={isSubmitting}
              isResendLoading={
                isResendBusy &&
                resendEmailNotification.variables ===
                  EmailNotificationResendType.daysAfterDueDate
              }
              isResendBusy={isResendBusy}
              onClick={() =>
                onResend(EmailNotificationResendType.daysAfterDueDate)
              }
              tooltipMessage={t('SETTINGS.TOOLTIPS.SAVE_TO_ENABLE_RESEND')}
              buttonText={t('ACTIONS.RESEND')}
              lastSentDate={emailLastSentQueries[1]?.data as string}
            />
          </div>

          <div className="notification-row__textarea">
            <textarea
              className="settings__section__item__textarea"
              {...methods.register(
                'daysAfterDueDateNotification',
                notificationContentRules
              )}
              placeholder={t('SETTINGS.LABELS.EMAIL_CONTENT') ?? ''}
            />
          </div>
        </div>

        {/* 3) XX days AFTER grace period */}
        <div className="settings__section">
          <h2 className="settings__section__title">
            {t('SETTINGS.TITLES.NOTIFICATION_AFTER_GRACE_PERIOD')}
          </h2>

          <div className="notification-row">
            <label className="settings__section__item__label notification-row__label">
              {t('SETTINGS.LABELS.DAYS_AFTER_GRACE_PERIOD')}
            </label>
            <Input
              name="daysAfterGracePeriod"
              className="settings__section__item__input notification-row__input"
              rules={daysRules}
              placeholder={t('SETTINGS.LABELS.DAYS_COUNT') ?? ''}
            />
            <ResendButton
              isDirty={isDirty}
              isEmpty={isEmpty(afterGracePeriodNotification)}
              isSubmitting={isSubmitting}
              isResendLoading={
                isResendBusy &&
                resendEmailNotification.variables ===
                  EmailNotificationResendType.daysAfterGracePeriod
              }
              isResendBusy={isResendBusy}
              onClick={() =>
                onResend(EmailNotificationResendType.daysAfterGracePeriod)
              }
              tooltipMessage={t('SETTINGS.TOOLTIPS.SAVE_TO_ENABLE_RESEND')}
              buttonText={t('ACTIONS.RESEND')}
              lastSentDate={emailLastSentQueries[2]?.data as string}
            />
          </div>

          <div className="notification-row__textarea">
            <textarea
              className="settings__section__item__textarea"
              {...methods.register(
                'daysAfterGracePeriodNotification',
                notificationContentRules
              )}
              placeholder={t('SETTINGS.LABELS.EMAIL_CONTENT') ?? ''}
            />
          </div>
        </div>

        <div className="settings__actions">
          <Button
            className="settings__actions__button"
            type="submit"
            loading={isSubmitting}
            disabled={!isDirty || hasErrors || isSubmitting}
          >
            {t('ACTIONS.SAVE')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
